import React, { useState, useEffect } from 'react';
import { getItemDataUsingQrCode } from '../../api/Item/itemApi';
import { getPlantByUserId } from '../../api/processing plant/plantApi';
import { getTruckDriver } from '../../api/TruckDriver/TruckDriver';
import { getCourierByUserId } from '../../api/courier/courierApi';
import { getInspectorById } from '../../api/BoatInspector/InspectorApi';
import { useLocation } from 'react-router-dom';

const ScannedFishDetails = () => {
  const [fishData, setFishData] = useState(null);
  const [plantName, setPlantName] = useState('N/A');
  const [truckDriverName, setTruckDriverName] = useState('N/A');
  const [courierName, setCourierName] = useState('N/A');
  const [inspectorName, setInspectorName] = useState('N/A'); // New state for inspector name
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const qrCode = location.pathname.split('/').filter(Boolean).pop() || '';

  useEffect(() => {
    const fetchFishData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch fish data using the real API
        const response = await getItemDataUsingQrCode(qrCode);
        console.log('Fetched Fish Data:', response.data.item);
        const itemData = response.data.item;
        setFishData(itemData);

        console.log('Plant Id:', itemData.processingPlantId);

        // Fetch plant name if processingPlantId exists
        if (itemData.processingPlantId) {
          try {
            const plantResponse = await getPlantByUserId(
              itemData.processingPlantId
            );
            setPlantName(plantResponse.data.companyName || 'Unknown Plant');
          } catch (plantErr) {
            console.error('Error fetching plant data:', plantErr);
            setPlantName('N/A');
          }
        }

        // Fetch truck driver name if truckDriverId exists
        if (itemData.truckDriverId) {
          try {
            const driverResponse = await getTruckDriver(itemData.truckDriverId);
            console.log('Truck Driver Data:', driverResponse.data);
            setTruckDriverName(driverResponse.data.name || 'Unknown Driver');
          } catch (driverErr) {
            console.error('Error fetching truck driver data:', driverErr);
            setTruckDriverName('N/A');
          }
        }

        // Fetch courier name if courierId exists
        if (itemData.courierId) {
          try {
            const courierResponse = await getCourierByUserId(
              itemData.courierId
            );
            console.log('Courier Data:', courierResponse.data);
            setCourierName(
              courierResponse.data.data.companyName || 'Unknown Courier'
            );
          } catch (courierErr) {
            console.error('Error fetching courier data:', courierErr);
            setCourierName('N/A');
          }
        }

        // Fetch inspector name if fisherInspectorId exists
        if (itemData.fisherInspectorId) {
          try {
            const inspectorResponse = await getInspectorById(
              itemData.fisherInspectorId
            );
            console.log('Inspector Data:', inspectorResponse.data);
            setInspectorName(
              inspectorResponse.data.name || 'Unknown Inspector'
            );
          } catch (inspectorErr) {
            console.error('Error fetching inspector data:', inspectorErr);
            setInspectorName('N/A');
          }
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Failed to fetch fish data'
        );
        console.error('Error fetching fish data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (qrCode) {
      fetchFishData();
    } else {
      setError('No QR code provided');
      setLoading(false);
    }
  }, [qrCode]);

  const formatFieldName = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .replace('Qr', 'QR')
      .replace('Id', 'ID');
  };

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return 'N/A';
    if (key.includes('Date') || key.includes('Datetime')) {
      return new Date(value).toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    }
    return value;
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      delivered: { badge: 'success', text: 'Delivered' },
      packaged: { badge: 'primary', text: 'Ready for Delivery' },
      processed: { badge: 'info', text: 'Processing Complete' },
      loaded: { badge: 'warning', text: 'In Transit' },
      inspected: { badge: 'info', text: 'Quality Approved' },
      scanned: { badge: 'success', text: 'Scanned' },
    };

    const normalizedStatus = status?.toLowerCase();
    return (
      statusMap[normalizedStatus] || { badge: 'secondary', text: 'Unknown' }
    );
  };

  const deliverySteps = [
    {
      icon: '🎣',
      title: 'Catch',
      description: 'Fresh caught by licensed fisher',
      field: 'catchDatetime',
      completed: !!fishData?.catchDatetime,
    },
    {
      icon: '🔍',
      title: 'Quality Check',
      description: 'Inspected for freshness & quality',
      field: 'inspectDate',
      completed: !!fishData?.inspectDate,
    },
    {
      icon: '🚛',
      title: 'Pickup',
      description: 'Loaded for transport',
      field: 'truckLoadedDate',
      completed: !!fishData?.truckLoadedDate,
    },
    {
      icon: '🏭',
      title: 'Processing',
      description: 'Cleaned and prepared',
      field: 'processingDate',
      completed: !!fishData?.processingDate,
    },
    {
      icon: '📦',
      title: 'Packaging',
      description: 'Sealed and labeled',
      field: 'packagingDate',
      completed: !!fishData?.packagingDate,
    },
    {
      icon: '🏠',
      title: 'Delivery',
      description: 'On its way to you!',
      field: 'deliveredDate',
      completed: !!fishData?.deliveredDate,
    },
  ];

  if (loading) {
    return (
      <>
        <link
          href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css"
          rel="stylesheet"
        />
        <div
          className="min-vh-100"
          style={{
            paddingTop: '100px',
          }}
        >
          <div className="container py-5">
            <div className="row justify-content-center align-items-center min-vh-75">
              <div className="col-12 col-md-6 col-lg-4">
                <div className="card shadow-lg border-0">
                  <div className="card-body text-center py-5">
                    <div
                      className="spinner-border text-primary mb-4"
                      role="status"
                      style={{ width: '3rem', height: '3rem' }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h4 className="card-title text-primary mb-3">
                      Loading Fish Details...
                    </h4>
                    <p className="card-text text-muted">
                      Please wait while we retrieve the information
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <link
          href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.3.0/css/bootstrap.min.css"
          rel="stylesheet"
        />
        <div
          className="min-vh-100"
          style={{
            paddingTop: '100px',
          }}
        >
          <div className="container py-5">
            <div className="row justify-content-center align-items-center min-vh-75">
              <div className="col-12 col-md-6 col-lg-4">
                <div className="card shadow-lg border-0 border-start border-danger border-4">
                  <div className="card-body text-center py-5">
                    <div className="display-1 mb-3">⚠️</div>
                    <h4 className="card-title text-danger mb-3">
                      Oops! Something went wrong
                    </h4>
                    <p className="card-text text-muted mb-4">{error}</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => window.location.reload()}
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const currentStatus =
    fishData?.packagingQrStatus ||
    fishData?.processingQrStatus ||
    fishData?.truckDriverQrStatus ||
    fishData?.fisherInspectorQrStatus ||
    'unknown';
  const statusInfo = getStatusInfo(currentStatus);

  return (
    <>
      <div className="min-vh-100">
        {/* Header */}
        <div
          className="bg-gradient text-white"
          style={{
            paddingTop: '100px',
          }}
        >
          <div className="container">
            <div className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-3">
                <span className="display-1 me-3">🐟</span>
                <h1 className="display-4 text-dark fw-bold mb-0">
                  Fish Tracker
                </h1>
              </div>
              <p className="lead text-dark opacity-75">
                From Ocean to Your Table - Complete Traceability
              </p>
            </div>
          </div>
        </div>

        <div
          className="container py-4"
          style={{ marginTop: '-2rem' }}
        >
          {fishData && (
            <>
              {/* Status Card */}
              <div className="card shadow-lg border-0 mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-12 col-md-8">
                      <h2 className="card-title h3 mb-2 text-dark">
                        {fishData.fishType} • {fishData.weight}kg
                      </h2>
                      <p className="card-text text-muted">
                        QR Code:{' '}
                        <code className="bg-light px-2 py-1 rounded">
                          {qrCode}
                        </code>
                      </p>
                    </div>
                    <div className="col-12 col-md-4 text-md-end mt-3 mt-md-0">
                      <span
                        className={`badge bg-${statusInfo.badge} px-3 py-2 fs-6`}
                      >
                        <i
                          className="bi bi-circle-fill me-1"
                          style={{ fontSize: '0.5rem' }}
                        ></i>
                        {statusInfo.text}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Progress Tracking */}
              <div className="card shadow-lg border-0 mb-4">
                <div className="card-header bg-white border-bottom">
                  <h3 className="card-title h5 mb-0">
                    <span className="me-2">📍</span>
                    Journey Progress
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {deliverySteps.map((step, index) => (
                      <div
                        key={index}
                        className="col-6 col-md-4 col-lg-2"
                      >
                        <div className="text-center">
                          <div className="position-relative mb-3">
                            <div
                              className={`rounded-circle mx-auto d-flex align-items-center justify-content-center ${
                                step.completed
                                  ? 'bg-success text-white shadow'
                                  : 'bg-light text-muted'
                              }`}
                              style={{
                                width: '60px',
                                height: '60px',
                                fontSize: '1.5rem',
                              }}
                            >
                              {step.completed ? '✓' : step.icon}
                            </div>
                          </div>
                          <h6
                            className={`fw-bold ${
                              step.completed ? 'text-dark' : 'text-muted'
                            }`}
                          >
                            {step.title}
                          </h6>
                          <p className="small text-muted mb-2">
                            {step.description}
                          </p>
                          {step.field && fishData[step.field] && (
                            <small className="text-success fw-bold">
                              {formatValue(fishData[step.field], step.field)}
                            </small>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="row g-4 mb-4">
                {/* Fisher Info */}
                <div className="col-12 col-lg-4">
                  <div className="card h-100 shadow border-0 border-start border-success border-4">
                    <div className="card-body">
                      <h5 className="card-title text-success mb-4">
                        <span className="me-2">🎣</span>
                        Fisher Information
                      </h5>
                      {/* <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">
                          Fisher ID
                        </label>
                        <p className="mb-0">
                          <code className="bg-light px-2 py-1 rounded">
                            {fishData.fisherInspectorId || 'N/A'}
                          </code>
                        </p>
                      </div> */}
                      <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">
                          Catch Location
                        </label>
                        {fishData.catchLocation || 'N/A'}
                      </div>
                      <div className="mb-0">
                        <label className="form-label small fw-bold text-muted">
                          Catch Time
                        </label>
                        {formatValue(fishData.catchDatetime, 'catchDatetime')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quality Info */}
                <div className="col-12 col-lg-4">
                  <div className="card h-100 shadow border-0 border-start border-info border-4">
                    <div className="card-body">
                      <h5 className="card-title text-info mb-4">
                        <span className="me-2">🔍</span>
                        Quality Assurance
                      </h5>
                      <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">
                          Inspector Name
                        </label>
                        <p className="mb-0 text-dark">{inspectorName}</p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">
                          Quality Grade
                        </label>
                        <p className="mb-0">
                          <span className="badge bg-success px-2 py-1">
                            {fishData.inspectQuality || 'N/A'}
                          </span>
                        </p>
                      </div>
                      <div className="mb-0">
                        <label className="form-label small fw-bold text-muted">
                          Inspection Date
                        </label>
                        <p className="mb-0 text-dark">
                          {formatValue(fishData.inspectDate, 'inspectDate')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Processing & Delivery Info */}
                <div className="col-12 col-lg-4">
                  <div className="card h-100 shadow border-0 border-start border-primary border-4">
                    <div className="card-body">
                      <h5 className="card-title text-primary mb-4">
                        <span className="me-2">🏭</span>
                        Processing & Delivery
                      </h5>
                      <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">
                          Processing Plant
                        </label>
                        <p className="mb-0 text-dark">{plantName}</p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">
                          Truck Driver
                        </label>
                        <p className="mb-0 text-dark">{truckDriverName}</p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label small fw-bold text-muted">
                          Courier
                        </label>
                        <p className="mb-0 text-dark">{courierName}</p>
                      </div>
                      <div className="mb-0">
                        <label className="form-label small fw-bold text-muted">
                          Processing Date
                        </label>
                        <p className="mb-0 text-dark">
                          {formatValue(
                            fishData.processingDate,
                            'processingDate'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Complete Details */}
              <div className="card shadow-lg border-0">
                <div className="card-header bg-white border-bottom">
                  <h3 className="card-title h5 mb-0">
                    <span className="me-2">📋</span>
                    Complete Details
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    {Object.entries({
                      ...fishData,
                      plantName,
                      truckDriverName,
                      courierName,
                      inspectorName, // Include inspectorName in complete details
                    })
                      .filter(([key]) => key !== '__v')
                      .map(([key, value]) => (
                        <div
                          key={key}
                          className="col-12 col-md-6 col-lg-4"
                        >
                          <div
                            className="p-3 bg-light rounded hover-shadow"
                            style={{ transition: 'all 0.2s ease' }}
                            onMouseEnter={(e) => {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow =
                                '0 4px 12px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = 'none';
                            }}
                          >
                            <label className="form-label small fw-bold text-muted mb-1">
                              {formatFieldName(key)}
                            </label>
                            <p className="mb-0 text-dark small">
                              {formatValue(value, key)}
                            </p>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {!fishData && (
            <div className="row justify-content-center align-items-center min-vh-50">
              <div className="col-12 col-md-6 col-lg-4">
                <div className="card shadow-lg border-0 border-start border-warning border-4">
                  <div className="card-body text-center py-5">
                    <div className="display-1 mb-4">🐟</div>
                    <h3 className="card-title text-dark mb-3">
                      No Fish Data Found
                    </h3>
                    <p className="card-text text-muted">
                      No information found for QR code: <br />
                      <code className="bg-light px-2 py-1 rounded text-primary">
                        {qrCode}
                      </code>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ScannedFishDetails;